import { createElement } from "react";
import { axiosInstance } from "../../../api/axiosInstance";
import { APIS } from "../../../modules/common/api/apis";
import { toFeeReceiptModel } from "./feeReceiptMapper";
import { PLACEHOLDER_SCHOOL, ReceiptModel, SchoolHeader } from "./receiptModel";

// Mirrors the backend's FeeReceipt record (GET /fee/receipts/{receiptNo})
export interface FeeReceiptData {
  receiptNo: number;
  name: string;
  admissionNo: string;
  grade: string;
  section: string;
  terms: string;
  receiptDate: string;
  paymentDate?: string | null;
  paymentMode: string;
  transactionId?: string | null;
  amount: number;
  discount: number;
  balance: number;
  receivedBy?: string | null; // the collecting admin's name
  academicYear?: string | null;
  balanceTerm?: number | null; // the term still owing when balance > 0
  paidOnlineBy?: string | null; // guardian who paid online
  school?: ReceiptSchool | null; // absent until the School Profile is saved
}

// Mirrors the backend's SchoolHeader record
export interface ReceiptSchool {
  name: string;
  address: string;
  phone?: string | null;
  email?: string | null;
  logo?: string | null; // data URL
}

export const toSchoolHeader = (school?: ReceiptSchool | null): SchoolHeader =>
  school
    ? {
        name: school.name,
        address: school.address,
        contact: [school.phone, school.email].filter(Boolean).join(" · ") || undefined,
        logo: school.logo ?? undefined,
      }
    : PLACEHOLDER_SCHOOL;

export const fetchFeeReceipt = async (receiptNo: number): Promise<FeeReceiptData> => {
  const response = await axiosInstance.get(APIS.FEE_RECEIPT.replace("receipt_no", String(receiptNo)));
  return response.data;
};

// Grade_Section_ReceiptNo.pdf, e.g. Nine_A_1042.pdf (anything that isn't safe in a file name becomes "-")
export const receiptFileName = (receipt: FeeReceiptData): string => {
  const safe = (part: string) => (part ?? "").trim().replace(/[^A-Za-z0-9-]+/g, "-") || "NA";
  return `${safe(receipt.grade)}_${safe(receipt.section)}_${receipt.receiptNo}.pdf`;
};

// The PDF library and fonts are large, so they load only when a receipt is first generated
export const renderReceiptPdf = async (receipt: ReceiptModel, school: SchoolHeader): Promise<Blob> => {
  const [{ pdf }, { ReceiptDocument, registerReceiptFonts }] = await Promise.all([import("@react-pdf/renderer"), import("./ReceiptDocument")]);
  registerReceiptFonts(`${window.location.origin}${process.env.PUBLIC_URL ?? ""}`);
  return pdf(createElement(ReceiptDocument, { receipt, school }) as any).toBlob();
};

export const buildReceiptPdf = (receipt: FeeReceiptData): Promise<Blob> => renderReceiptPdf(toFeeReceiptModel(receipt), toSchoolHeader(receipt.school));
